import React from 'react'
import { IconAngleRight, IconAnnouncement, IconGlobe, IconShare, IconTeacherOnly } from '../../icons/library'
import { Badge, BadgeStyle } from '../../components/simple/badge'
import { RemoteHTMLTooltip } from '../../components/simple/tooltip'
import { UserType } from '../../providers/context'

export type LanguageGlobe = { opacity: number; tooltip: string }

type BadgeType = {
  name: string
  tooltip: string
  hoverForDetails?: boolean
}
export type TagsInfo = {
  messageID: number
  sender?: { name: string }
  announcement?: boolean
  caret?: boolean
  recipients?: { name: string }[]
  badges?: {
    student?: BadgeType
    guardian?: BadgeType
    teacher?: { showIcon: boolean }
  }
  globe: LanguageGlobe
  teacherOnly: boolean
}

export const MessageTags: React.FC<{
  tags: TagsInfo
  extras?: React.ReactNode
  read?: boolean
  fixedSize?: boolean
  simplify?: boolean
  className?: string
}> = ({ tags, extras, className, read = true, fixedSize = false, simplify = false }) => {
  const combinedRecipients = tags.recipients?.map((recipient) => recipient.name).join(', ')

  return (
    <div
      className={`${className} flex ${fixedSize ? '' : 'flex-1'} flex-nowrap items-center gap-2 overflow-hidden text-nowrap ${read ? '' : 'font-bold'}`}
    >
      {tags.sender && (
        <span className={`overflow-hidden text-ellipsis ${!read ? 'font-bold' : ''}`}>{tags.sender.name}</span>
      )}
      {tags.announcement && <IconAnnouncement size="lg" />}
      {tags.caret && <IconAngleRight size="xs" thicken={30} />}
      {combinedRecipients && (
        <span className={`overflow-hidden text-ellipsis ${!read ? 'font-bold' : ''}`}>{combinedRecipients}</span>
      )}
      {tags.teacherOnly && <IconTeacherOnly tooltip="This message is only visible to teachers" />}
      {tags.badges?.student && (
        <TooltipBadgeWrapper
          badge={tags.badges.student}
          badgeStyle={tags.teacherOnly ? BadgeStyle.light : BadgeStyle.student}
          loadFrom={{ url: '/messageRecipients', data: { id: tags.messageID, type: 'students' } }}
        />
      )}
      {tags.badges?.guardian && (
        <TooltipBadgeWrapper
          badge={tags.badges.guardian}
          badgeStyle={tags.teacherOnly ? BadgeStyle.light : BadgeStyle.guardian}
          loadFrom={{ url: '/messageRecipients', data: { id: tags.messageID, type: 'parents' } }}
        />
      )}
      {tags.badges?.teacher && (
        <RemoteHTMLTooltip loadFrom={{ url: '/messageRecipients', data: { id: tags.messageID, type: 'teachers' } }}>
          <Badge>{tags.badges.teacher.showIcon ? <IconShare /> : 'staff'}</Badge>
        </RemoteHTMLTooltip>
      )}
      {tags.globe && <LanguageGlobeIcon {...tags.globe} />}
      {extras}
    </div>
  )
}

const TooltipBadgeWrapper: React.FC<{
  badge: BadgeType
  badgeStyle: BadgeStyle
  loadFrom: React.ComponentProps<typeof RemoteHTMLTooltip>['loadFrom']
}> = ({ badge, badgeStyle, loadFrom }) => {
  const badgeRendered = (
    <Badge badgeStyle={badgeStyle} tooltip={badge.tooltip}>
      {badge.name}
    </Badge>
  )
  if (badge.hoverForDetails) return <RemoteHTMLTooltip loadFrom={loadFrom}>{badgeRendered}</RemoteHTMLTooltip>
  return badgeRendered
}

export const LanguageGlobeIcon: React.FC<LanguageGlobe> = ({ opacity, tooltip }) => (
  <IconGlobe opacity={opacity} tooltip={tooltip} />
)

export const UserBadge: React.FC<{ name: string; type: UserType }> = ({ name, type }) => (
  <Badge
    className="text-nowrap"
    badgeStyle={
      type === UserType.Student
        ? BadgeStyle.student
        : type === UserType.Guardian
          ? BadgeStyle.guardian
          : BadgeStyle.light
    }
  >
    {name}
  </Badge>
)
